'use client';

import { RefObject, useImperativeHandle, useState } from 'react';

type TParam<TRawParam> = TRawParam & { isOpen: boolean; _key: number };

export type TOverlayRef<TRawParam> = {
  param: TParam<TRawParam>;
  open: (__newParam: TRawParam) => void;
  update: (__newParam: Partial<TRawParam>) => void;
  close: () => void;
};

export const useOverlayImperativeHandle = <TRawParam>(
  ref: RefObject<TOverlayRef<TRawParam>>,
  rawDefaultParam: TRawParam
): TOverlayRef<TRawParam> => {
  const defaultParam = { ...rawDefaultParam, isOpen: false, _key: 0 };

  const [param, setParam] = useState<TParam<TRawParam>>(defaultParam);

  const open: TOverlayRef<TRawParam>['open'] = (newParam) => {
    setParam(({ _key }) => ({
      ...defaultParam,
      ...newParam,
      _key: _key + 1,
      isOpen: true,
    }));
  };

  const close: TOverlayRef<TRawParam>['close'] = () => {
    setParam({ ...defaultParam, isOpen: false });
  };

  const update: TOverlayRef<TRawParam>['update'] = (newParam) => {
    setParam((prevParam) => ({ ...prevParam, ...newParam }));
  };

  const refProps: TOverlayRef<TRawParam> = { param, open, close, update };

  useImperativeHandle(ref, () => refProps, []);

  return refProps;
};

export const getRefMethods = <TRawParam>(
  ref: RefObject<TOverlayRef<TRawParam>>
): Pick<TOverlayRef<TRawParam>, 'open' | 'close' | 'update'> => ({
  open: (param) => ref?.current?.open?.(param),
  close: () => ref?.current?.close?.(),
  update: (param) => ref?.current?.update?.(param),
});
